import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useFormik } from "formik";
import {
  createLead,
  updateLead,
  inviteLead,
} from "../../../services/lead.service";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse  } from "date-fns";
import * as Yup from "yup";

type LeadData = {
  id?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  businessName?: string;
  email?: string;
  phone?: string;
  businessEIN?: string;
  businessStreet?: string;
  businessCity?: string;
  businessState?: string;
  businessZipcode?: string;
  comments?: string;
  followUpDate?: string;
};

type AddLeadModalProps = {
  open: boolean;
  handleClose: (action?: string) => void;
  editMode?: boolean;
  leadData?: LeadData;
  user?: string;
};

const getValidationSchema = (inviteMode: boolean) =>
  Yup.object()
    .shape({
      firstName: Yup.string()
        .min(1, "First Name is required")
        .required("First Name is required"),
      middleName: Yup.string().optional(),
      lastName: Yup.string().optional(),
      businessName: Yup.string().required("Business Name is required"),
      email: Yup.string().required("Email is required").email("Invalid email"),
      phone: Yup.string().matches(
        /^\d{10}$/,
        "Phone must be exactly 10 digits"
      ),
      businessEIN: Yup.string()
        .matches(/^\d{9}$/, "EIN must be a 9-digit number")
        .optional(),
      businessStreet: Yup.string().optional(),
      businessCity: Yup.string().optional(),
      businessState: Yup.string().optional(),
      businessZipcode: Yup.string().optional(),
      comments: Yup.string().optional(),
      followUpDate: Yup.string()
        .required("Follow Up Date is required")
        .test(
          "not-in-past",
          "Follow Up Date cannot be in the past",
          (value) => {
            if (!value) return false;
            const selected = new Date(value);
            selected.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return selected >= today;
          }
        ),
    })
    .test(
      "invite-email-or-phone-required",
      "Either Email or Phone Number is required for invite",
      function (value) {
        if (!inviteMode) return true;
        const { email, phone } = value || {};
        const hasEmail = !!(email && email.trim() !== "");
        const hasPhone = !!(phone && phone.trim() !== "");
        return (
          hasEmail ||
          hasPhone ||
          this.createError({
            message: "Either Email or Phone Number is required for invite",
          })
        );
      }
    );

const AddLeadModal = ({
  open,
  handleClose,
  editMode = false,
  leadData,
  user,
}: AddLeadModalProps) => {
  const [loading, setLoading] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [snackbar, setSnackbar] = useState<{

    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      businessName: "",
      email: "",
      phone: "",
      businessEIN: "",
      businessStreet: "",
      businessCity: "",
      businessState: "",
      businessZipcode: "",
      comments: "",
      followUpDate: "",
    },
    validationSchema: getValidationSchema(inviteMode),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        if (inviteMode) {
          await inviteLead(values);
          setSnackbar({
            open: true,
            message: "Lead invited successfully!",
            severity: "success",
          });
        } else if (editMode && leadData?.id) {
          await updateLead(leadData.id, values);
          setSnackbar({
            open: true,
            message: "Lead updated successfully!",
            severity: "success",
          });
        } else {
          await createLead(values);
          setSnackbar({
            open: true,
            message: "Lead added successfully!",
            severity: "success",
          });
        }
        handleClose("reload");
        resetForm();
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error?.message || "Operation failed.",
          severity: "error",
        });
      } finally {
        setLoading(false);
        setInviteMode(false);
      }
    },
  });

  useEffect(() => {
    formik.setFormikState((prev) => ({
      ...prev,
      validationSchema: getValidationSchema(inviteMode),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteMode]);

  useEffect(() => {
    if (open) {
      setInviteMode(false);
      if (editMode && leadData) {
        let formattedFollowUpDate = "";
        if (leadData.followUpDate) {
            formattedFollowUpDate = format(new Date(leadData.followUpDate),"yyyy-MM-dd");
        }
        formik.setValues({
          firstName: leadData.firstName || "",
          middleName: leadData.middleName || "",
          lastName: leadData.lastName || "",
          businessName: leadData.businessName || "",
          email: leadData.email || "",
          phone: leadData.phone || "",
          businessEIN: leadData.businessEIN || "",
          businessStreet: leadData.businessStreet || "",
          businessCity: leadData.businessCity || "",
          businessState: leadData.businessState || "",
          businessZipcode: leadData.businessZipcode || "",
          comments: leadData.comments || "",
          followUpDate: formattedFollowUpDate,
        });
      } else {
        formik.resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editMode, leadData]);

  // Show invite cross-field error under both fields if needed
  const showInviteEmailOrPhoneError =
    inviteMode &&
    typeof formik.errors === "object" &&
    (formik.errors as any)["invite-email-or-phone-required"] &&
    (formik.touched.email || formik.touched.phone || formik.submitCount > 0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      maxWidth={isMobile ? false : "md"}
      

      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose("cancel");
        }
      }}
      fullWidth
      disableEscapeKeyDown
      PaperProps={{

        sx: {
          height: isMobile ? "100vh" : "auto",
          maxHeight: isMobile ? "100vh" : "none",
          m: isMobile ? 0 : undefined,
          borderRadius: isMobile ? 0 : undefined,
        },
      }}
    >

      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          component="span"
          className="font-bold text-gray-800"
        >
          {editMode ? "Edit Lead" : "Create Lead"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => handleClose("cancel")}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <DialogContent
          className="custom-scrollbar"
          sx={{
            flex: 1,
            overflowY: "auto",
          }}
        >
          <div className="space-y-4 py-2">
            <Grid container spacing={2}>

            <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Lead First Name *"
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter first name"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Lead Middle Name"
              name="middleName"
              value={formik.values.middleName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter middle name"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Lead Last Name"
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter last name"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Business Name *"
              name="businessName"
              value={formik.values.businessName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.businessName &&
                Boolean(formik.errors.businessName)
              }
              helperText={
                formik.touched.businessName && formik.errors.businessName
              }
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter business name"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Email *"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.email && Boolean(formik.errors.email)) ||
                showInviteEmailOrPhoneError
              }
              helperText={
                (formik.touched.email && formik.errors.email) ||
                (showInviteEmailOrPhoneError &&
                  "Either Email or Phone Number is required for invite")
              }
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter email address"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Phone Number (optional)"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.phone && Boolean(formik.errors.phone)) ||
                showInviteEmailOrPhoneError
              }
              helperText={
                (formik.touched.phone && formik.errors.phone) ||
                (showInviteEmailOrPhoneError &&
                  "Either Email or Phone Number is required for invite")
              }
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter phone number"
            />
            </Grid>
            {inviteMode && (
            <Grid size={{ xs: 12, md: 12 }}>
              <Typography variant="caption" color="error">
                * Either Email or Phone Number is required for invite
              </Typography>
            </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Business EIN (optional)"
              name="businessEIN"
              value={formik.values.businessEIN}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter business EIN"
              error={
                formik.touched.businessEIN && Boolean(formik.errors.businessEIN)
              }
              helperText={
                formik.touched.businessEIN && formik.errors.businessEIN
              }
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Business Street"
              name="businessStreet"
              value={formik.values.businessStreet}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter street address"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Business City"
              name="businessCity"
              value={formik.values.businessCity}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter city"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Business State"
              name="businessState"
              value={formik.values.businessState}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter state"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Business Zipcode"
              name="businessZipcode"
              value={formik.values.businessZipcode}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter zipcode"
            />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Follow Up Date *"
                value={
                  formik.values.followUpDate
                    ? parse(
                        formik.values.followUpDate,
                        "yyyy-MM-dd",
                        new Date()
                      )
                    : null
                }
                onChange={(date) =>
                  formik.setFieldValue(
                    "followUpDate",
                    date ? format(date, "yyyy-MM-dd") : ""
                  )
                }
                format="MM-dd-yyyy"
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: "MM-DD-YYYY",
                    InputLabelProps: { shrink: true },
                    error: formik.touched.followUpDate && Boolean(formik.errors.followUpDate),
                    helperText: formik.touched.followUpDate && formik.errors.followUpDate,
                    onBlur: () => formik.setFieldTouched("followUpDate", true),
                    InputProps: {
                      sx: {
                        borderRadius: "10px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "10px",
                        },
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Comments (optional)"
              name="comments"
              value={formik.values.comments}
              onChange={formik.handleChange}
              InputProps={{ style: { borderRadius: 10 } }}
              placeholder="Enter comments"
              multiline
              minRows={1}
            />
            </Grid>
          </Grid>
          </div>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <Button
            color="error"
            variant="outlined"
            onClick={() => handleClose("cancel")}
            startIcon={<CloseIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            disabled={loading}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
            }}
            onClick={() => setInviteMode(false)}
          >
            {editMode ? "Update" : "Create"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<MailOutlineIcon />}
            disabled={loading}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
            }}
            onClick={() => {
              setInviteMode(true);
              setTimeout(() => {
                formik.handleSubmit();
              }, 0);
            }}
          >
            Invite
          </Button>
        </DialogActions>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddLeadModal;
